import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { FeatherClock, FeatherPlus } from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";

import type {
  EventRecordFollowUpActionResponseDto,
  EventRecordUserSummaryDto,
  FollowUpActionResponseDtoStatus,
} from "@/generated/model";
import { getActionStatusBadge } from "@/lib/follow-up-action-utils";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";

interface LinkedFollowUpActionsProps {
  actions: EventRecordFollowUpActionResponseDto[];
  /** Whether the current user may add corrective tasks to this record. */
  canCreate: boolean;
  onCreate: () => void;
}

/**
 * Corrective follow-up tasks attached to an event record (NAU-221). Renders
 * from the record detail's embedded `followUpActions` projection so it can show
 * completion date + completing user without an extra fetch, and stays in step
 * with the PDF export which reads the same projection.
 */
export function LinkedFollowUpActions({
  actions,
  canCreate,
  onCreate,
}: LinkedFollowUpActionsProps) {
  const completedCount = actions.filter(
    (action) => action.status === "COMPLETED",
  ).length;

  return (
    <section className="flex w-full flex-col items-start gap-3">
      <div className="flex w-full items-center gap-2">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Corrective follow-up tasks
        </span>
        {actions.length > 0 && (
          <Badge variant="neutral">
            {completedCount} of {actions.length} complete
          </Badge>
        )}
        {canCreate && (
          <Button
            className="ml-auto"
            variant="neutral-secondary"
            icon={<FeatherPlus />}
            onClick={onCreate}
          >
            Create follow-up task
          </Button>
        )}
      </div>

      {actions.length === 0 ? (
        <div className="flex w-full flex-col items-start rounded-md border border-dashed border-neutral-200 px-4 py-6">
          <span className="text-body font-body text-subtext-color">
            No corrective tasks yet.
            {canCreate
              ? " Create a follow-up task for any issue this drill identified."
              : ""}
          </span>
        </div>
      ) : (
        <ul className="flex w-full flex-col gap-2">
          {actions.map((action) => (
            <li key={action.id} className="w-full">
              <Link
                to="/follow-up-actions/$actionId"
                params={{ actionId: action.id }}
                className="flex w-full items-start justify-between gap-4 rounded-md border border-neutral-200 bg-white px-4 py-3 hover:border-neutral-400 hover:shadow-sm transition"
              >
                <ActionSummary action={action} />
                <div className="flex shrink-0 items-center gap-2">
                  {getActionStatusBadge(
                    // The two DTOs share identical status string values; the
                    // generated enum types are nominally distinct, so bridge them.
                    action.status as unknown as FollowUpActionResponseDtoStatus,
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ActionSummary({
  action,
}: {
  action: EventRecordFollowUpActionResponseDto;
}) {
  const assignee = action.assigneeUser
    ? userLabel(action.assigneeUser)
    : action.assigneeRole
      ? (NURSERY_ROLE_LABELS[action.assigneeRole] ?? action.assigneeRole)
      : null;

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 min-w-0">
      <span className="text-body-bold font-body-bold text-neutral-900 w-full">
        {action.description}
      </span>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1 text-caption font-caption text-subtext-color">
          <FeatherClock className="h-3 w-3" />
          {formatDue(action.dueDate, action.dueTime)}
        </span>
        {assignee && (
          <span className="text-caption font-caption text-subtext-color">
            · {assignee}
          </span>
        )}
      </div>
      {action.completedAt && (
        <span className="text-caption font-caption text-success-700">
          Completed {format(new Date(action.completedAt), "d MMM yyyy")}
          {action.completedBy && <> by {userLabel(action.completedBy)}</>}
          {action.completionNotes && <> — {action.completionNotes}</>}
        </span>
      )}
    </div>
  );
}

function formatDue(
  dueDate: string | null | undefined,
  dueTime: string | null | undefined,
): string {
  if (!dueDate) return "No due date";
  const date = format(new Date(dueDate), "d MMM yyyy");
  return dueTime ? `Due ${date} at ${dueTime}` : `Due ${date}`;
}

function userLabel(u: EventRecordUserSummaryDto): string {
  return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email;
}
